import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Eye, Users, Globe, Smartphone, Monitor,
  TrendingUp, MapPin, Image, ExternalLink, Activity, Clock, Trash2, Bot
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-accent' }) => (
  <div className="bg-card border border-border rounded-lg p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-lg bg-accent/10 ${color}`}>
        <Icon size={18} />
      </div>
      <span className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</span>
    </div>
    <p className="font-serif text-3xl font-light tracking-tight">{value}</p>
    {sub && <p className="font-sans text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const BarChart = ({ data, labelKey, valueKey, maxBars = 10 }) => {
  if (!data || data.length === 0) return <p className="text-sm text-muted-foreground">No data yet</p>;
  const items = data.slice(0, maxBars);
  const max = Math.max(...items.map(d => d[valueKey]), 1);
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="font-sans text-xs text-muted-foreground w-32 truncate flex-shrink-0" title={item[labelKey]}>
            {item[labelKey] || 'Unknown'}
          </span>
          <div className="flex-1 h-6 bg-muted/50 rounded overflow-hidden">
            <div
              className="h-full bg-accent/70 rounded transition-all duration-500"
              style={{ width: `${(item[valueKey] / max) * 100}%` }}
            />
          </div>
          <span className="font-mono text-xs text-foreground w-10 text-right flex-shrink-0">{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
};

const MiniLineChart = ({ data }) => {
  if (!data || data.length < 2) return <p className="text-sm text-muted-foreground">Not enough data for chart</p>;
  const max = Math.max(...data.map(d => d.views), 1);
  const width = 100;
  const height = 40;
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * width,
    y: height - (d.views / max) * (height - 4),
  }));
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-24" preserveAspectRatio="none">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.3" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#areaGrad)" />
      <path d={line} fill="none" stroke="var(--accent)" strokeWidth="1" strokeLinecap="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="1.2" fill="var(--accent)" />
      ))}
    </svg>
  );
};

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [resetting, setResetting] = useState(false);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadData();
  }, [token, navigate, days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/dashboard?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('This will permanently delete ALL analytics data. Are you sure?')) return;
    setResetting(true);
    try {
      const res = await axios.delete(`${API}/analytics/reset`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      loadData();
    } catch {
      toast.error('Failed to reset analytics');
    } finally {
      setResetting(false);
    }
  };

  const handlePurgeBots = async () => {
    if (!window.confirm('Remove all bot/test traffic from analytics?')) return;
    setResetting(true);
    try {
      const res = await axios.delete(`${API}/analytics/reset-bots`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      loadData();
    } catch {
      toast.error('Failed to purge bot traffic');
    } finally {
      setResetting(false);
    }
  };

  const deviceTotal = data ? (data.devices.mobile || 0) + (data.devices.desktop || 0) : 0;
  const mobilePercent = deviceTotal > 0 ? Math.round(((data?.devices.mobile || 0) / deviceTotal) * 100) : 0;

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="analytics-dashboard-page">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-2 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div className="flex items-end justify-between mb-8 sm:mb-12 flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight" data-testid="analytics-title">
              Analytics
            </h1>
            <p className="font-sans text-sm text-muted-foreground mt-1">
              Visitor insights and artwork engagement
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  days === d ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`period-${d}`}
              >
                {d}d
              </button>
            ))}
            <div className="w-px h-5 bg-border mx-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={handlePurgeBots}
              disabled={resetting}
              className="text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="purge-bots-btn"
            >
              <Bot size={12} className="mr-1" />
              Purge Bot Traffic
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetAll}
              disabled={resetting}
              className="text-xs border-red-200 text-red-700 hover:bg-red-50"
              data-testid="reset-analytics-btn"
            >
              <Trash2 size={12} className="mr-1" />
              Reset All
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : !data ? (
          <p className="text-muted-foreground">No analytics data available.</p>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <StatCard icon={Eye} label="Total Views" value={data.total_views.toLocaleString()} sub={`Last ${days} days`} />
              <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors.toLocaleString()} sub={`${days}-day period`} />
              <StatCard
                icon={Smartphone}
                label="Mobile"
                value={`${mobilePercent}%`}
                sub={`${data.devices.mobile || 0} mobile / ${data.devices.desktop || 0} desktop`}
              />
              <StatCard
                icon={Image}
                label="Artwork Views"
                value={(data.top_artworks || []).reduce((s, a) => s + a.views, 0).toLocaleString()}
                sub={`${(data.top_artworks || []).length} paintings viewed`}
              />
            </div>

            {data.bot_views_filtered > 0 && (
              <div className="flex items-center gap-2 mb-6 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="bot-filter-info">
                <Bot size={14} className="text-amber-600 flex-shrink-0" />
                <span className="font-sans text-xs text-amber-700">
                  {data.bot_views_filtered} bot/test views filtered out from these stats.
                </span>
              </div>
            )}

            {/* Views Over Time */}
            <div className="bg-card border border-border rounded-lg p-5 sm:p-6 mb-6" data-testid="views-chart">
              <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-4 flex items-center gap-2">
                <TrendingUp size={16} className="text-accent" /> Views Over Time
              </h2>
              <MiniLineChart data={data.daily_views} />
              {data.daily_views.length > 0 && (
                <div className="flex justify-between mt-2 text-[10px] text-muted-foreground font-mono">
                  <span>{data.daily_views[0]?.date}</span>
                  <span>{data.daily_views[data.daily_views.length - 1]?.date}</span>
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Top Pages */}
              <div className="bg-card border border-border rounded-lg p-5 sm:p-6" data-testid="top-pages">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-4 flex items-center gap-2">
                  <Activity size={16} className="text-accent" /> Top Pages
                </h2>
                <BarChart data={data.top_pages} labelKey="page" valueKey="views" />
              </div>

              {/* Most Viewed Artworks */}
              <div className="bg-card border border-border rounded-lg p-5 sm:p-6" data-testid="top-artworks">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-4 flex items-center gap-2">
                  <Image size={16} className="text-accent" /> Most Viewed Paintings
                </h2>
                <BarChart data={data.top_artworks} labelKey="title" valueKey="views" />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* By Country */}
              <div className="bg-card border border-border rounded-lg p-5 sm:p-6" data-testid="by-country">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-4 flex items-center gap-2">
                  <Globe size={16} className="text-accent" /> Visitors by Country
                </h2>
                <BarChart data={data.by_country} labelKey="country" valueKey="views" />
              </div>

              {/* By City */}
              <div className="bg-card border border-border rounded-lg p-5 sm:p-6" data-testid="by-city">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-4 flex items-center gap-2">
                  <MapPin size={16} className="text-accent" /> Top Cities
                </h2>
                <BarChart
                  data={(data.by_city || []).map(c => ({ label: `${c.city}, ${c.country}`, views: c.views }))}
                  labelKey="label"
                  valueKey="views"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Referrer Sources */}
              <div className="bg-card border border-border rounded-lg p-5 sm:p-6" data-testid="referrers">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-4 flex items-center gap-2">
                  <ExternalLink size={16} className="text-accent" /> Traffic Sources
                </h2>
                {(data.referrers || []).length > 0 ? (
                  <BarChart data={data.referrers} labelKey="source" valueKey="count" />
                ) : (
                  <p className="text-sm text-muted-foreground">No referrer data yet — visitors are arriving directly.</p>
                )}
              </div>

              {/* Device Breakdown */}
              <div className="bg-card border border-border rounded-lg p-5 sm:p-6" data-testid="device-breakdown">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-4 flex items-center gap-2">
                  <Monitor size={16} className="text-accent" /> Devices
                </h2>
                <div className="flex gap-6 mt-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Monitor size={18} className="text-muted-foreground" />
                      <span className="font-sans text-sm">Desktop</span>
                    </div>
                    <p className="font-serif text-2xl font-light">{data.devices.desktop || 0}</p>
                    <div className="w-full h-2 bg-muted rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-accent/60 rounded-full" style={{ width: `${100 - mobilePercent}%` }} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Smartphone size={18} className="text-muted-foreground" />
                      <span className="font-sans text-sm">Mobile</span>
                    </div>
                    <p className="font-serif text-2xl font-light">{data.devices.mobile || 0}</p>
                    <div className="w-full h-2 bg-muted rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-accent/60 rounded-full" style={{ width: `${mobilePercent}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="recent-activity">
              <div className="px-5 py-4 border-b border-border">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground flex items-center gap-2">
                  <Clock size={16} className="text-accent" /> Recent Activity
                </h2>
              </div>
              <div className="divide-y divide-border max-h-80 overflow-y-auto">
                {(data.recent_activity || []).length === 0 ? (
                  <p className="p-5 text-sm text-muted-foreground">No recent activity</p>
                ) : (
                  data.recent_activity.map((a, i) => (
                    <div key={i} className="px-5 py-3 flex items-center gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.artwork_id ? 'bg-violet-500' : 'bg-accent'}`} />
                      <span className="font-sans font-medium w-28 truncate flex-shrink-0">{a.page}</span>
                      {a.artwork_title && (
                        <span className="text-xs text-violet-600 truncate max-w-40">{a.artwork_title}</span>
                      )}
                      <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto flex-shrink-0">
                        <MapPin size={10} /> {a.city}{a.country && a.country !== 'Local' ? `, ${a.country}` : ''}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0 w-14 text-right">
                        {a.device === 'mobile' ? '📱' : '🖥'}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
                        {new Date(a.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
